// SPDX-FileCopyrightText: Copyright Orangebot, Inc.
// SPDX-License-Identifier: Apache-2.0

/**
 * VideoCallOverlay Component
 *
 * Enterprise healthcare telehealth video call interface.
 * Displays video feeds, patient clinical context, and professional controls.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Paper,
  Portal,
  ActionIcon,
  Badge,
  Tooltip,
  Divider,
  Transition,
  ThemeIcon,
  Indicator,
  Center,
  ScrollArea,
  Textarea,
  Button,
  Modal,
  TextInput,
  Tabs,
  Loader,
  useMantineColorScheme,
} from '@mantine/core';
import {
  IconClock,
  IconWifi,
  IconWifiOff,
  IconRecordMail,
  IconShieldCheck,
  IconStethoscope,
  IconNotes,
  IconMessageCircle,
  IconMaximize,
  IconMinimize,
  IconLayoutSidebar,
  IconLayoutSidebarRight,
  IconAlertTriangle,
  IconHeart,
  IconActivity,
  IconDroplet,
  IconX,
  IconGridDots,
  IconFocusCentered,
  IconPill,
  IconFlask,
  IconExternalLink,
  IconSend,
  IconChevronDown,
  IconChevronRight,
  IconTemperature,
  IconLungs,
  IconScale,
  IconRuler,
  IconSun,
  IconMoon,
  IconMicrophone,
  IconMicrophoneOff,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlayerStop,
  IconWand,
  IconCheck,
  IconAlertCircle,
  IconPictureInPicture,
  IconPictureInPictureOff,
  IconMenu2,
} from '@tabler/icons-react';
import { getDisplayString } from '@medplum/core';
import { useTranslation } from 'react-i18next';
import { useMedplum } from '@medplum/react';
import type {
  Observation,
  Condition,
  MedicationRequest,
  AllergyIntolerance,
} from '@medplum/fhirtypes';
import { useCareComm } from '../../contexts/CareCommContext';
import { useAIRecording } from '../../contexts/AIRecordingContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { CallControls } from './CallControls';
import { agoraService } from '../../services/agoraService';
import { AuthenticatedAvatar } from '../AuthenticatedAvatar';
import { getPatientPhotoUrl } from '../../utils/imageUtils';
import classes from './VideoCallOverlay.module.css';

// Container IDs for video elements
const LOCAL_VIDEO_CONTAINER_ID = 'carecomm-local-video';
const REMOTE_VIDEO_CONTAINER_ID = 'carecomm-remote-video';

// Network quality type
type NetworkQuality = 'excellent' | 'good' | 'fair' | 'poor' | 'unknown';

// LOINC codes for vitals
const VITAL_LOINC_CODES = {
  bloodPressure: '85354-9',
  heartRate: '8867-4',
  respiratoryRate: '9279-1',
  temperature: '8310-5',
  weight: '29463-7',
  height: '8302-2',
  bmi: '39156-5',
  spO2: '59408-5',
  spO2Arterial: '2708-6',
  painLevel: '72514-3',
};

interface VitalDisplay {
  id: string;
  name: string;
  value: string;
  date: string;
  icon: typeof IconHeart;
  color: string;
}

interface ProblemDisplay {
  id: string;
  name: string;
  status?: string;
}

interface MedicationDisplay {
  id: string;
  name: string;
  status?: string;
}

interface AllergyDisplay {
  id: string;
  name: string;
  severity?: string;
}

interface LabDisplay {
  id: string;
  name: string;
  value: string;
  date: string;
}

interface ChatMessage {
  id: string;
  sender: 'provider' | 'patient';
  text: string;
  timestamp: Date;
}

// Sidebar tab types
type SidebarTab = 'clinical' | 'chat' | 'notes' | 'transcription';

/**
 * Calculate relative luminance of a hex color
 * Returns true if the color is light (needs dark text)
 */
function isLightBackground(hexColor: string): boolean {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate relative luminance using sRGB formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return true if luminance is above threshold (light background needs dark text)
  return luminance > 0.5;
}

/**
 * Get the current theme color from CSS custom property
 */
function getThemeColor(): string {
  if (typeof window === 'undefined') return '#553a48';
  const color = getComputedStyle(document.documentElement)
    .getPropertyValue('--carepoint-theme-color')
    .trim();
  return color || '#553a48';
}

export function VideoCallOverlay() {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const { isOpen: isSidebarOpen, toggleOpen: toggleAppSidebar } = useSidebar();
  const {
    activeCall,
    endCall,
    toggleMute,
    toggleCamera,
    toggleScreenShare,
    settings,
    enterPiPMode,
    minimizeCall,
  } = useCareComm();

  // AI Recording context for transcription
  const {
    session: aiSession,
    aiSettings,
    isSupported: isRecordingSupported,
    startRecording: startAIRecording,
    stopRecording: stopAIRecording,
    pauseRecording: pauseAIRecording,
    resumeRecording: resumeAIRecording,
    cancelRecording: cancelAIRecording,
    transcriptionJobs,
    getTranscriptionJob,
    dismissTranscriptionJob,
    setOnTranscriptionComplete,
  } = useAIRecording();

  const localVideoRef = useRef<HTMLDivElement>(null);
  const remoteVideoRef = useRef<HTMLDivElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);

  // UI State
  const [showSidebar, setShowSidebar] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [networkQuality, setNetworkQuality] = useState<NetworkQuality>('good');
  const [isRecording] = useState(settings.recordingEnabled);
  const [viewMode, setViewMode] = useState<'spotlight' | 'grid'>('spotlight');
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>('clinical');
  const [hasLightBackground, setHasLightBackground] = useState(false);

  // Clinical data state
  const [isLoadingClinical, setIsLoadingClinical] = useState(true);
  const [vitals, setVitals] = useState<VitalDisplay[]>([]);
  const [problems, setProblems] = useState<ProblemDisplay[]>([]);
  const [medications, setMedications] = useState<MedicationDisplay[]>([]);
  const [allergies, setAllergies] = useState<AllergyDisplay[]>([]);
  const [labs, setLabs] = useState<LabDisplay[]>([]);

  // Collapsible sections
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['vitals', 'allergies', 'problems'])
  );

  // Notes modal state
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);

  // Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');

  // Transcription state
  const [transcriptionText, setTranscriptionText] = useState('');
  const [generatedAINote, setGeneratedAINote] = useState('');

  // Auto-hide controls after inactivity
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Toggle section expansion
  const toggleSection = useCallback((section: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  }, []);

  // Format call duration
  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  // Format short date
  const formatShortDate = (dateString?: string): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '';
    }
  };

  // Get vital icon and color based on LOINC code
  const getVitalIconInfo = (code: string): { icon: typeof IconHeart; color: string } => {
    switch (code) {
      case VITAL_LOINC_CODES.heartRate:
        return { icon: IconHeart, color: 'red' };
      case VITAL_LOINC_CODES.bloodPressure:
        return { icon: IconActivity, color: 'blue' };
      case VITAL_LOINC_CODES.spO2:
      case VITAL_LOINC_CODES.spO2Arterial:
        return { icon: IconDroplet, color: 'cyan' };
      case VITAL_LOINC_CODES.temperature:
        return { icon: IconTemperature, color: 'orange' };
      case VITAL_LOINC_CODES.respiratoryRate:
        return { icon: IconLungs, color: 'teal' };
      case VITAL_LOINC_CODES.weight:
        return { icon: IconScale, color: 'grape' };
      case VITAL_LOINC_CODES.height:
        return { icon: IconRuler, color: 'indigo' };
      default:
        return { icon: IconActivity, color: 'gray' };
    }
  };

  // Format observation value
  const formatObservationValue = (obs: Observation): string => {
    if (obs.valueQuantity) {
      const value = obs.valueQuantity.value;
      const unit = obs.valueQuantity.unit || obs.valueQuantity.code || '';
      return `${value} ${unit}`.trim();
    }
    if (obs.valueCodeableConcept) {
      return obs.valueCodeableConcept.coding?.[0]?.display || obs.valueCodeableConcept.text || '';
    }
    if (obs.valueString) {
      return obs.valueString;
    }
    // Handle blood pressure (systolic/diastolic components)
    if (obs.component && obs.component.length > 0) {
      const systolicComp = obs.component.find((c) => c.code?.coding?.[0]?.code === '8480-6');
      const diastolicComp = obs.component.find((c) => c.code?.coding?.[0]?.code === '8462-4');
      if (systolicComp && diastolicComp) {
        const systolic = systolicComp.valueQuantity?.value;
        const diastolic = diastolicComp.valueQuantity?.value;
        return `${systolic}/${diastolic} mmHg`;
      }
    }
    return 'N/A';
  };

  // Network quality indicator
  const getNetworkQualityInfo = (quality: NetworkQuality) => {
    switch (quality) {
      case 'excellent':
        return { color: 'green', icon: IconWifi, label: 'Excellent' };
      case 'good':
        return { color: 'teal', icon: IconWifi, label: 'Good' };
      case 'fair':
        return { color: 'yellow', icon: IconWifi, label: 'Fair' };
      case 'poor':
        return { color: 'red', icon: IconWifiOff, label: 'Poor' };
      default:
        return { color: 'gray', icon: IconWifi, label: 'Unknown' };
    }
  };

  // Detect theme color for header text contrast
  useEffect(() => {
    const themeColor = getThemeColor();
    setHasLightBackground(isLightBackground(themeColor));
  }, [activeCall]); // Re-check when call becomes active

  // Load patient clinical context
  useEffect(() => {
    const loadPatientContext = async () => {
      if (!activeCall?.patient?.id) return;

      setIsLoadingClinical(true);

      try {
        // Fetch all clinical data in parallel
        const [vitalResults, conditionResults, medicationResults, allergyResults, labResults] =
          await Promise.all([
            // Vitals
            medplum.searchResources('Observation', {
              patient: `Patient/${activeCall.patient.id}`,
              code: Object.values(VITAL_LOINC_CODES).join(','),
              _sort: '-date',
              _count: '50',
            }),
            // Conditions (Problems)
            medplum.searchResources('Condition', {
              subject: `Patient/${activeCall.patient.id}`,
              'clinical-status': 'active',
              _sort: '-onset-date',
              _count: '20',
            }),
            // Medications
            medplum.searchResources('MedicationRequest', {
              subject: `Patient/${activeCall.patient.id}`,
              status: 'active',
              _sort: '-authoredon',
              _count: '20',
            }),
            // Allergies
            medplum.searchResources('AllergyIntolerance', {
              patient: `Patient/${activeCall.patient.id}`,
              'clinical-status': 'active',
              _count: '20',
            }),
            // Labs
            medplum.searchResources('Observation', {
              subject: `Patient/${activeCall.patient.id}`,
              category: 'laboratory',
              _sort: '-date',
              _count: '10',
            }),
          ]);

        // Process vitals - get latest for each type
        const latestByCode = new Map<string, Observation>();
        for (const obs of vitalResults) {
          const code = obs.code?.coding?.[0]?.code;
          if (code && !latestByCode.has(code)) {
            latestByCode.set(code, obs);
          }
        }

        const vitalsList: VitalDisplay[] = [];
        for (const [code, obs] of latestByCode.entries()) {
          const { icon, color } = getVitalIconInfo(code);
          vitalsList.push({
            id: obs.id || '',
            name: obs.code?.coding?.[0]?.display || obs.code?.text || 'Unknown',
            value: formatObservationValue(obs),
            date: formatShortDate(obs.effectiveDateTime),
            icon,
            color,
          });
        }
        setVitals(vitalsList);

        // Process problems
        const problemsList: ProblemDisplay[] = conditionResults
          .filter((c: Condition) => {
            const verificationCode = c.verificationStatus?.coding?.[0]?.code;
            return verificationCode !== 'unconfirmed' && verificationCode !== 'entered-in-error';
          })
          .map((c: Condition) => ({
            id: c.id || '',
            name: c.code?.coding?.[0]?.display || c.code?.text || 'Unknown',
            status: c.clinicalStatus?.coding?.[0]?.code,
          }));
        setProblems(problemsList);

        // Process medications
        const medicationsList: MedicationDisplay[] = medicationResults
          .filter((m: MedicationRequest) => m.status !== 'entered-in-error')
          .map((m: MedicationRequest) => ({
            id: m.id || '',
            name:
              m.medicationCodeableConcept?.coding?.[0]?.display ||
              m.medicationCodeableConcept?.text ||
              'Unknown',
            status: m.status,
          }));
        setMedications(medicationsList);

        // Process allergies
        const allergiesList: AllergyDisplay[] = allergyResults.map((a: AllergyIntolerance) => ({
          id: a.id || '',
          name: a.code?.coding?.[0]?.display || a.code?.text || 'Unknown',
          severity: a.criticality,
        }));
        setAllergies(allergiesList);

        // Process labs
        const labsList: LabDisplay[] = labResults.map((obs: Observation) => ({
          id: obs.id || '',
          name: obs.code?.coding?.[0]?.display || obs.code?.text || 'Unknown',
          value: formatObservationValue(obs),
          date: formatShortDate(obs.effectiveDateTime),
        }));
        setLabs(labsList);
      } catch (error) {
        console.warn('Failed to load patient context:', error);
      } finally {
        setIsLoadingClinical(false);
      }
    };

    loadPatientContext();
  }, [activeCall?.patient?.id, medplum]);

  // Play remote video when available
  useEffect(() => {
    if (activeCall?.state === 'connected' && remoteVideoRef.current) {
      agoraService.playRemoteVideo(REMOTE_VIDEO_CONTAINER_ID);
    }
  }, [activeCall?.state]);

  // Replay local video when camera is toggled back on
  useEffect(() => {
    if (
      activeCall?.state === 'connected' &&
      !activeCall?.isCameraOff &&
      activeCall?.type === 'video-call'
    ) {
      // Small delay to ensure the container is rendered in the DOM
      const timer = setTimeout(() => {
        agoraService.playLocalVideo(LOCAL_VIDEO_CONTAINER_ID);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [activeCall?.state, activeCall?.isCameraOff, activeCall?.type]);

  // Auto-hide controls
  const resetControlsTimeout = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (activeCall?.state === 'connected') {
        setShowControls(false);
      }
    }, 5000);
  }, [activeCall?.state]);

  useEffect(() => {
    resetControlsTimeout();
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [resetControlsTimeout]);

  // Set up transcription complete callback
  useEffect(() => {
    setOnTranscriptionComplete((transcription: string, generatedNote: string) => {
      setTranscriptionText(transcription);
      setGeneratedAINote(generatedNote);
      // Also add to the notes content for easy editing
      if (generatedNote) {
        setNoteContent((prev) => (prev ? `${prev}\n\n--- AI Generated Note ---\n${generatedNote}` : generatedNote));
      }
    });

    return () => {
      setOnTranscriptionComplete(undefined);
    };
  }, [setOnTranscriptionComplete]);

  // Store stopAIRecording in a ref for cleanup
  const stopAIRecordingRef = useRef(stopAIRecording);
  stopAIRecordingRef.current = stopAIRecording;

  // Store aiSession in a ref for cleanup
  const aiSessionRef = useRef(aiSession);
  aiSessionRef.current = aiSession;

  // Stop AI recording when the video call ends
  useEffect(() => {
    // If the call is no longer active but we have an AI recording session, stop it
    if (!activeCall && aiSession && (aiSession.state === 'recording' || aiSession.state === 'paused')) {
      console.log('Video call ended, stopping AI recording...');
      stopAIRecording();
    }
  }, [activeCall, aiSession, stopAIRecording]);

  // Cleanup on unmount - stop recording if still active
  useEffect(() => {
    return () => {
      const session = aiSessionRef.current;
      if (session && (session.state === 'recording' || session.state === 'paused')) {
        console.log('VideoCallOverlay unmounting, stopping AI recording...');
        stopAIRecordingRef.current();
      }
    };
  }, []);

  // Check for completed transcription jobs for this encounter
  const currentTranscriptionJob = activeCall?.encounterId
    ? getTranscriptionJob(activeCall.encounterId)
    : undefined;

  // Handle starting transcription recording
  const handleStartTranscription = useCallback(async () => {
    if (!activeCall?.encounterId || !activeCall?.patient) {
      console.warn('Cannot start transcription: missing encounter or patient');
      return;
    }

    // Create a minimal Encounter object for the AI recording context
    const encounter = {
      resourceType: 'Encounter' as const,
      id: activeCall.encounterId,
      status: 'in-progress' as const,
      class: { code: 'VR', display: 'Virtual' },
      subject: { reference: `Patient/${activeCall.patient.id}` },
    };

    await startAIRecording(encounter, activeCall.patient);
  }, [activeCall?.encounterId, activeCall?.patient, startAIRecording]);

  // Handle applying AI note to visit notes
  const handleApplyAINote = useCallback(() => {
    if (generatedAINote) {
      setNoteContent((prev) => (prev ? `${prev}\n\n${generatedAINote}` : generatedAINote));
      setSidebarTab('notes');
    }
  }, [generatedAINote]);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  // Open patient chart in new tab
  const handleViewChart = useCallback(() => {
    if (activeCall?.patient?.id) {
      window.open(`/Patient/${activeCall.patient.id}`, '_blank');
    }
  }, [activeCall?.patient?.id]);

  // Save note
  const handleSaveNote = useCallback(async () => {
    if (!noteContent.trim() || !activeCall?.patient?.id) return;

    setIsSavingNote(true);
    try {
      // Create a clinical note as a DocumentReference or add to encounter notes
      const noteData = {
        resourceType: 'DocumentReference' as const,
        status: 'current' as const,
        type: {
          coding: [
            {
              system: 'http://loinc.org',
              code: '11506-3',
              display: 'Provider-unspecified progress note',
            },
          ],
        },
        subject: { reference: `Patient/${activeCall.patient.id}` },
        date: new Date().toISOString(),
        description: noteTitle || 'Telehealth Visit Note',
        content: [
          {
            attachment: {
              contentType: 'text/plain',
              data: btoa(noteContent),
              title: noteTitle || 'Telehealth Visit Note',
            },
          },
        ],
        context: activeCall.encounterId
          ? { encounter: [{ reference: `Encounter/${activeCall.encounterId}` }] }
          : undefined,
      };

      await medplum.createResource(noteData);

      // Reset form and close modal
      setNoteTitle('');
      setNoteContent('');
      setShowNotesModal(false);
    } catch (error) {
      console.error('Failed to save note:', error);
    } finally {
      setIsSavingNote(false);
    }
  }, [noteContent, noteTitle, activeCall?.patient?.id, activeCall?.encounterId, medplum]);

  // Send chat message
  const handleSendChat = useCallback(() => {
    if (!chatInput.trim()) return;

    const newMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      sender: 'provider',
      text: chatInput,
      timestamp: new Date(),
    };

    setChatMessages((prev) => [...prev, newMessage]);
    setChatInput('');

    // Scroll to bottom
    setTimeout(() => {
      chatScrollRef.current?.scrollTo({
        top: chatScrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }, 100);

    // TODO: Send via Agora RTM or similar real-time messaging
  }, [chatInput]);

  // Don't render if no active call or if in PiP/minimized mode
  if (!activeCall || activeCall.displayMode !== 'fullscreen') {
    return null;
  }

  const isVideoCall = activeCall.type === 'video-call';
  const patientName = getDisplayString(activeCall.patient);
  const patientInitials = patientName
    .split(' ')
    .map((n) => n.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2);
  const patientPhotoUrl = getPatientPhotoUrl(activeCall.patient);
  const networkInfo = getNetworkQualityInfo(networkQuality);

  // Collapsible section header component
  const SectionHeader = ({
    id,
    icon: Icon,
    iconColor,
    title,
    count,
  }: {
    id: string;
    icon: typeof IconHeart;
    iconColor: string;
    title: string;
    count?: number;
  }) => (
    <Group
      gap="xs"
      py={8}
      px={4}
      style={{ cursor: 'pointer' }}
      onClick={() => toggleSection(id)}
    >
      {expandedSections.has(id) ? (
        <IconChevronDown size={14} color="#868e96" />
      ) : (
        <IconChevronRight size={14} color="#868e96" />
      )}
      <Icon size={14} color={`var(--mantine-color-${iconColor}-5)`} />
      <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ flex: 1 }}>
        {title} {count !== undefined && `(${count})`}
      </Text>
    </Group>
  );

  return (
    <Portal>
      <Box
        className={classes.overlay}
        onMouseMove={resetControlsTimeout}
        onClick={resetControlsTimeout}
      >
        {/* Top Header Bar */}
        <Transition mounted={showControls} transition="slide-down" duration={200}>
          {(styles) => (
            <Box
              className={classes.header}
              style={styles}
              data-light-bg={hasLightBackground ? 'true' : undefined}
            >
              <Group justify="space-between" h="100%">
                {/* Left: Menu Toggle, Logo & Patient Info */}
                <Group gap="md">
                  {/* App Navigation Controls */}
                  <Group gap="xs">
                    <Tooltip label="Navigate (switches to PiP mode)">
                      <ActionIcon
                        variant="subtle"
                        size="lg"
                        className={classes.headerIconButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('[VideoCallOverlay] Menu toggle clicked, switching to PiP mode');
                          // Switch to PiP mode and open sidebar so user can navigate
                          enterPiPMode();
                          if (!isSidebarOpen) {
                            toggleAppSidebar();
                          }
                        }}
                      >
                        <IconMenu2 size={20} />
                      </ActionIcon>
                    </Tooltip>
                    <img
                      src="/img/app-logo-wide.png"
                      alt="CarePoint"
                      className={classes.headerLogo}
                    />
                  </Group>

                  <Divider orientation="vertical" color="gray.7" />

                  <Indicator
                    color={activeCall.state === 'connected' ? 'green' : 'yellow'}
                    position="bottom-end"
                    size={12}
                    processing={activeCall.state === 'connecting'}
                  >
                    <AuthenticatedAvatar size={44} radius="xl" color="blue" src={patientPhotoUrl}>
                      {patientInitials}
                    </AuthenticatedAvatar>
                  </Indicator>
                  <Stack gap={2}>
                    <Group gap="xs">
                      <Text fw={600} size="md" className={classes.headerText}>
                        {patientName}
                      </Text>
                      {isRecording && (
                        <Badge
                          size="xs"
                          color="red"
                          variant="filled"
                          leftSection={<IconRecordMail size={10} />}
                        >
                          REC
                        </Badge>
                      )}
                    </Group>
                    <Group gap="xs">
                      <Text size="xs" className={classes.headerTextMuted}>
                        {isVideoCall
                          ? t('carecomm.videoCall', 'Video Consultation')
                          : t('carecomm.voiceCall', 'Voice Call')}
                      </Text>
                      {activeCall.encounterId && (
                        <>
                          <Text size="xs" className={classes.headerTextMuted}>
                            •
                          </Text>
                          <Text size="xs" className={classes.headerTextMuted}>
                            {t('carecomm.encounterLinked', 'Encounter linked')}
                          </Text>
                        </>
                      )}
                    </Group>
                  </Stack>
                </Group>

                {/* Center: Call Duration & Status */}
                <Group gap="lg">
                  {activeCall.state === 'connected' && (
                    <Paper px="md" py={6} radius="xl" className={classes.durationBadge}>
                      <Group gap="xs">
                        <IconClock size={14} />
                        <Text size="sm" fw={600}>
                          {formatDuration(activeCall.duration)}
                        </Text>
                      </Group>
                    </Paper>
                  )}

                  {/* Network Quality */}
                  <Tooltip label={`Connection: ${networkInfo.label}`}>
                    <Paper
                      px="sm"
                      py={6}
                      radius="xl"
                      className={classes.networkBadge}
                      data-quality={networkQuality}
                    >
                      <Group gap={6}>
                        <networkInfo.icon size={14} />
                        <Text size="xs" fw={500}>
                          {networkInfo.label}
                        </Text>
                      </Group>
                    </Paper>
                  </Tooltip>
                </Group>

                {/* Right: Actions */}
                <Group gap="xs">
                  <Tooltip label={showSidebar ? 'Hide sidebar' : 'Show sidebar'}>
                    <ActionIcon
                      variant="subtle"
                      size="lg"
                      className={classes.headerIconButton}
                      onClick={() => setShowSidebar(!showSidebar)}
                    >
                      {showSidebar ? (
                        <IconLayoutSidebarRight size={20} />
                      ) : (
                        <IconLayoutSidebar size={20} />
                      )}
                    </ActionIcon>
                  </Tooltip>

                  <Tooltip label={viewMode === 'spotlight' ? 'Grid view' : 'Spotlight view'}>
                    <ActionIcon
                      variant="subtle"
                      size="lg"
                      className={classes.headerIconButton}
                      onClick={() => setViewMode(viewMode === 'spotlight' ? 'grid' : 'spotlight')}
                    >
                      {viewMode === 'spotlight' ? (
                        <IconGridDots size={20} />
                      ) : (
                        <IconFocusCentered size={20} />
                      )}
                    </ActionIcon>
                  </Tooltip>

                  <Tooltip label="Picture in Picture">
                    <ActionIcon
                      variant="subtle"
                      size="lg"
                      className={`${classes.headerIconButton} ${classes.pipButton}`}
                      onClick={enterPiPMode}
                    >
                      <IconPictureInPicture size={20} />
                    </ActionIcon>
                  </Tooltip>

                  <Tooltip label="Minimize to footer">
                    <ActionIcon
                      variant="subtle"
                      size="lg"
                      className={`${classes.headerIconButton} ${classes.minimizeButton}`}
                      onClick={minimizeCall}
                    >
                      <IconChevronDown size={20} />
                    </ActionIcon>
                  </Tooltip>

                  <Tooltip label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
                    <ActionIcon
                      variant="subtle"
                      size="lg"
                      className={classes.headerIconButton}
                      onClick={toggleFullscreen}
                    >
                      {isFullscreen ? <IconMinimize size={20} /> : <IconMaximize size={20} />}
                    </ActionIcon>
                  </Tooltip>

                  <Tooltip label={colorScheme === 'dark' ? 'Light mode' : 'Dark mode'}>
                    <ActionIcon
                      variant="subtle"
                      size="lg"
                      className={classes.headerIconButton}
                      onClick={() => toggleColorScheme()}
                    >
                      {colorScheme === 'dark' ? <IconSun size={20} /> : <IconMoon size={20} />}
                    </ActionIcon>
                  </Tooltip>

                  <Divider orientation="vertical" color="gray.7" />

                  <Tooltip label="HIPAA Compliant">
                    <ThemeIcon variant="subtle" color="green" size="lg">
                      <IconShieldCheck size={20} />
                    </ThemeIcon>
                  </Tooltip>
                </Group>
              </Group>
            </Box>
          )}
        </Transition>

        {/* Main Content Area */}
        <Box className={classes.mainContent}>
          {/* Video Area */}
          <Box className={classes.videoArea} data-has-sidebar={showSidebar}>
            {/* Remote Video (Full area background) or Voice Call Avatar */}
            {isVideoCall ? (
              <Box
                id={REMOTE_VIDEO_CONTAINER_ID}
                ref={remoteVideoRef}
                className={classes.remoteVideo}
              >
                {/* Placeholder when no remote video */}
                <Center className={classes.remoteVideoPlaceholder}>
                  <Stack align="center" gap="md">
                    <AuthenticatedAvatar size={120} radius="xl" color="blue" src={patientPhotoUrl}>
                      {patientInitials}
                    </AuthenticatedAvatar>
                    <Text c="gray.4" size="lg">
                      {activeCall.state === 'connecting'
                        ? t('carecomm.connectingToPatient', 'Connecting to patient...')
                        : t('carecomm.waitingForVideo', 'Waiting for patient video...')}
                    </Text>
                  </Stack>
                </Center>
              </Box>
            ) : (
              /* Voice call - centered avatar */
              <Center className={classes.voiceCallCenter}>
                <Stack align="center" gap="lg">
                  <Box className={classes.voiceAvatarContainer}>
                    <AuthenticatedAvatar
                      size={160}
                      radius="xl"
                      color="blue"
                      className={classes.voiceAvatar}
                      src={patientPhotoUrl}
                    >
                      {patientInitials}
                    </AuthenticatedAvatar>
                    {activeCall.state === 'connected' && <Box className={classes.voiceWave} />}
                  </Box>
                  <Text size="xl" fw={600} c="white">
                    {patientName}
                  </Text>
                  {activeCall.state === 'connecting' && (
                    <Text c="gray.4" size="lg">
                      {t('carecomm.connecting', 'Connecting...')}
                    </Text>
                  )}
                  {activeCall.state === 'ringing' && (
                    <Text c="gray.4" size="lg">
                      {t('carecomm.ringing', 'Ringing...')}
                    </Text>
                  )}
                </Stack>
              </Center>
            )}

            {/* Local Video PIP */}
            {isVideoCall && !activeCall.isCameraOff && (
              <Paper shadow="xl" radius="md" className={classes.localVideoPip}>
                <Box
                  id={LOCAL_VIDEO_CONTAINER_ID}
                  ref={localVideoRef}
                  className={classes.localVideoInner}
                />
                <Box className={classes.localVideoLabel}>
                  <Text size="xs" c="white" fw={500}>
                    You
                  </Text>
                </Box>
              </Paper>
            )}

            {/* Connecting Overlay */}
            {activeCall.state === 'connecting' && (
              <Box className={classes.connectingOverlay}>
                <Stack align="center" gap="md">
                  <Box className={classes.spinner} />
                  <Text c="white" size="lg" fw={500}>
                    {t('carecomm.establishingConnection', 'Establishing secure connection...')}
                  </Text>
                </Stack>
              </Box>
            )}
          </Box>

          {/* Clinical Sidebar */}
          <Transition mounted={showSidebar} transition="slide-left" duration={200}>
            {(styles) => (
              <Box className={classes.sidebar} style={styles}>
                <Stack gap={0} h="100%">
                  {/* Sidebar Header with Tabs */}
                  <Box className={classes.sidebarHeader}>
                    <Group justify="space-between" mb="sm">
                      <Group gap="xs">
                        <IconStethoscope size={18} />
                        <Text fw={600} size="sm">
                          {patientName}
                        </Text>
                      </Group>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        onClick={() => setShowSidebar(false)}
                      >
                        <IconX size={16} />
                      </ActionIcon>
                    </Group>

                    <Tabs
                      value={sidebarTab}
                      onChange={(value) => setSidebarTab(value as SidebarTab)}
                      variant="pills"
                      radius="xl"
                    >
                      <Tabs.List grow>
                        <Tabs.Tab value="clinical" leftSection={<IconStethoscope size={14} />}>
                          Clinical
                        </Tabs.Tab>
                        <Tabs.Tab
                          value="transcription"
                          leftSection={
                            aiSession?.state === 'recording' ? (
                              <IconMicrophone size={14} color="var(--mantine-color-red-5)" />
                            ) : (
                              <IconWand size={14} />
                            )
                          }
                        >
                          AI
                        </Tabs.Tab>
                        <Tabs.Tab value="chat" leftSection={<IconMessageCircle size={14} />}>
                          Chat
                        </Tabs.Tab>
                        <Tabs.Tab value="notes" leftSection={<IconNotes size={14} />}>
                          Notes
                        </Tabs.Tab>
                      </Tabs.List>
                    </Tabs>
                  </Box>

                  {/* Tab Content */}
                  <ScrollArea style={{ flex: 1 }} p="sm">
                    {/* Clinical Tab */}
                    {sidebarTab === 'clinical' && (
                      <Stack gap={0}>
                        {isLoadingClinical ? (
                          <Center py="xl">
                            <Loader size="sm" />
                          </Center>
                        ) : (
                          <>
                            {/* Allergies Section (Always visible for safety) */}
                            <Box>
                              <SectionHeader
                                id="allergies"
                                icon={IconAlertTriangle}
                                iconColor="orange"
                                title="Allergies"
                                count={allergies.length}
                              />
                              {expandedSections.has('allergies') && (
                                <Paper p="xs" radius="md" className={classes.infoCard} mb="sm">
                                  {allergies.length > 0 ? (
                                    <Stack gap={4}>
                                      {allergies.map((allergy) => (
                                        <Group key={allergy.id} gap="xs">
                                          <IconAlertTriangle
                                            size={12}
                                            color="var(--mantine-color-orange-5)"
                                          />
                                          <Text size="xs" c="orange.4" style={{ flex: 1 }}>
                                            {allergy.name}
                                          </Text>
                                          {allergy.severity === 'high' && (
                                            <Badge size="xs" color="red" variant="light">
                                              Severe
                                            </Badge>
                                          )}
                                        </Group>
                                      ))}
                                    </Stack>
                                  ) : (
                                    <Group gap="xs" justify="center">
                                      <IconShieldCheck
                                        size={14}
                                        color="var(--mantine-color-green-5)"
                                      />
                                      <Text size="xs" c="green.5">
                                        No known allergies
                                      </Text>
                                    </Group>
                                  )}
                                </Paper>
                              )}
                            </Box>

                            {/* Vitals Section */}
                            <Box>
                              <SectionHeader
                                id="vitals"
                                icon={IconHeart}
                                iconColor="red"
                                title="Recent Vitals"
                                count={vitals.length}
                              />
                              {expandedSections.has('vitals') && (
                                <Paper p="xs" radius="md" className={classes.infoCard} mb="sm">
                                  {vitals.length > 0 ? (
                                    <Stack gap={6}>
                                      {vitals.map((vital) => (
                                        <Group key={vital.id} justify="space-between">
                                          <Group gap="xs">
                                            <vital.icon
                                              size={12}
                                              color={`var(--mantine-color-${vital.color}-5)`}
                                            />
                                            <Text size="xs" c="dimmed">
                                              {vital.name}
                                            </Text>
                                          </Group>
                                          <Group gap={4}>
                                            <Text size="xs" fw={600}>
                                              {vital.value}
                                            </Text>
                                            {vital.date && (
                                              <Text size="xs" c="dimmed">
                                                ({vital.date})
                                              </Text>
                                            )}
                                          </Group>
                                        </Group>
                                      ))}
                                    </Stack>
                                  ) : (
                                    <Text size="xs" c="dimmed" ta="center">
                                      No recent vitals
                                    </Text>
                                  )}
                                </Paper>
                              )}
                            </Box>

                            {/* Problems Section */}
                            <Box>
                              <SectionHeader
                                id="problems"
                                icon={IconStethoscope}
                                iconColor="orange"
                                title="Problem List"
                                count={problems.length}
                              />
                              {expandedSections.has('problems') && (
                                <Paper p="xs" radius="md" className={classes.infoCard} mb="sm">
                                  {problems.length > 0 ? (
                                    <Stack gap={4}>
                                      {problems.slice(0, 8).map((problem) => (
                                        <Group key={problem.id} gap="xs">
                                          <Text size="xs" style={{ flex: 1 }}>
                                            {problem.name}
                                          </Text>
                                          {problem.status === 'active' && (
                                            <Badge size="xs" color="red" variant="light">
                                              Active
                                            </Badge>
                                          )}
                                        </Group>
                                      ))}
                                      {problems.length > 8 && (
                                        <Text size="xs" c="dimmed" ta="center">
                                          +{problems.length - 8} more
                                        </Text>
                                      )}
                                    </Stack>
                                  ) : (
                                    <Text size="xs" c="dimmed" ta="center">
                                      No active problems
                                    </Text>
                                  )}
                                </Paper>
                              )}
                            </Box>

                            {/* Medications Section */}
                            <Box>
                              <SectionHeader
                                id="medications"
                                icon={IconPill}
                                iconColor="violet"
                                title="Medications"
                                count={medications.length}
                              />
                              {expandedSections.has('medications') && (
                                <Paper p="xs" radius="md" className={classes.infoCard} mb="sm">
                                  {medications.length > 0 ? (
                                    <Stack gap={4}>
                                      {medications.slice(0, 8).map((med) => (
                                        <Group key={med.id} gap="xs">
                                          <IconPill
                                            size={12}
                                            color="var(--mantine-color-violet-5)"
                                          />
                                          <Text size="xs" style={{ flex: 1 }} lineClamp={1}>
                                            {med.name}
                                          </Text>
                                        </Group>
                                      ))}
                                      {medications.length > 8 && (
                                        <Text size="xs" c="dimmed" ta="center">
                                          +{medications.length - 8} more
                                        </Text>
                                      )}
                                    </Stack>
                                  ) : (
                                    <Text size="xs" c="dimmed" ta="center">
                                      No active medications
                                    </Text>
                                  )}
                                </Paper>
                              )}
                            </Box>

                            {/* Labs Section */}
                            <Box>
                              <SectionHeader
                                id="labs"
                                icon={IconFlask}
                                iconColor="teal"
                                title="Recent Labs"
                                count={labs.length}
                              />
                              {expandedSections.has('labs') && (
                                <Paper p="xs" radius="md" className={classes.infoCard} mb="sm">
                                  {labs.length > 0 ? (
                                    <Stack gap={4}>
                                      {labs.slice(0, 5).map((lab) => (
                                        <Group key={lab.id} justify="space-between">
                                          <Text size="xs" c="dimmed" style={{ flex: 1 }}>
                                            {lab.name}
                                          </Text>
                                          <Text size="xs" fw={600}>
                                            {lab.value}
                                          </Text>
                                        </Group>
                                      ))}
                                    </Stack>
                                  ) : (
                                    <Text size="xs" c="dimmed" ta="center">
                                      No recent labs
                                    </Text>
                                  )}
                                </Paper>
                              )}
                            </Box>
                          </>
                        )}
                      </Stack>
                    )}

                    {/* Chat Tab */}
                    {sidebarTab === 'chat' && (
                      <Stack gap="sm" h="100%">
                        <ScrollArea style={{ flex: 1 }} ref={chatScrollRef}>
                          <Stack gap="xs">
                            {chatMessages.length > 0 ? (
                              chatMessages.map((msg) => (
                                <Paper
                                  key={msg.id}
                                  p="xs"
                                  radius="md"
                                  className={classes.infoCard}
                                  style={{
                                    marginLeft: msg.sender === 'provider' ? 'auto' : 0,
                                    marginRight: msg.sender === 'patient' ? 'auto' : 0,
                                    maxWidth: '85%',
                                    backgroundColor:
                                      msg.sender === 'provider'
                                        ? 'var(--mantine-color-blue-9)'
                                        : undefined,
                                  }}
                                >
                                  <Text size="xs">{msg.text}</Text>
                                  <Text size="xs" c="dimmed" ta="right">
                                    {msg.timestamp.toLocaleTimeString([], {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </Text>
                                </Paper>
                              ))
                            ) : (
                              <Text size="xs" c="dimmed" ta="center" py="xl">
                                No messages yet. Start a conversation with the patient.
                              </Text>
                            )}
                          </Stack>
                        </ScrollArea>

                        <Group gap="xs">
                          <Textarea
                            placeholder="Type a message..."
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            minRows={1}
                            maxRows={3}
                            autosize
                            className={classes.sidebarTextarea}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendChat();
                              }
                            }}
                          />
                          <ActionIcon
                            variant="filled"
                            color="blue"
                            size="lg"
                            onClick={handleSendChat}
                            disabled={!chatInput.trim()}
                          >
                            <IconSend size={18} />
                          </ActionIcon>
                        </Group>
                      </Stack>
                    )}

                    {/* Notes Tab */}
                    {sidebarTab === 'notes' && (
                      <Stack gap="sm">
                        <Text size="xs" c="dimmed">
                          Quick notes during the call. These will be saved to the patient's record.
                        </Text>

                        <Textarea
                          placeholder="Add notes during the call..."
                          value={noteContent}
                          onChange={(e) => setNoteContent(e.target.value)}
                          minRows={8}
                          maxRows={12}
                          autosize
                          className={classes.sidebarTextarea}
                        />

                        <Button
                          leftSection={<IconNotes size={16} />}
                          onClick={() => setShowNotesModal(true)}
                          disabled={!noteContent.trim()}
                          variant="light"
                        >
                          Save Note
                        </Button>
                      </Stack>
                    )}

                    {/* Transcription/AI Tab */}
                    {sidebarTab === 'transcription' && (
                      <Stack gap="md">
                        {/* AI Notes Feature Status */}
                        {!aiSettings?.enabled ? (
                          <Paper p="md" radius="md" className={classes.infoCard}>
                            <Stack align="center" gap="sm">
                              <IconAlertCircle size={32} color="var(--mantine-color-yellow-5)" />
                              <Text size="sm" ta="center">
                                AI Notes is not enabled for your organization.
                              </Text>
                              <Text size="xs" c="dimmed" ta="center">
                                Contact your administrator to enable AI-powered transcription.
                              </Text>
                            </Stack>
                          </Paper>
                        ) : !isRecordingSupported ? (
                          <Paper p="md" radius="md" className={classes.infoCard}>
                            <Stack align="center" gap="sm">
                              <IconAlertCircle size={32} color="var(--mantine-color-red-5)" />
                              <Text size="sm" ta="center">
                                Audio recording is not supported in this browser.
                              </Text>
                            </Stack>
                          </Paper>
                        ) : !activeCall?.encounterId ? (
                          <Paper p="md" radius="md" className={classes.infoCard}>
                            <Stack align="center" gap="sm">
                              <IconAlertCircle size={32} color="var(--mantine-color-yellow-5)" />
                              <Text size="sm" ta="center">
                                No encounter linked to this call.
                              </Text>
                              <Text size="xs" c="dimmed" ta="center">
                                AI transcription requires an encounter to be associated with the call.
                              </Text>
                            </Stack>
                          </Paper>
                        ) : (
                          <>
                            {/* Recording Controls */}
                            <Paper p="md" radius="md" className={classes.infoCard}>
                              <Stack gap="sm">
                                <Group justify="space-between">
                                  <Text size="sm" fw={600}>
                                    AI Transcription
                                  </Text>
                                  {aiSession?.state === 'recording' && (
                                    <Badge color="red" variant="filled" leftSection={<IconRecordMail size={10} />}>
                                      Recording
                                    </Badge>
                                  )}
                                  {aiSession?.state === 'paused' && (
                                    <Badge color="yellow" variant="filled">
                                      Paused
                                    </Badge>
                                  )}
                                  {aiSession?.state === 'processing' && (
                                    <Badge color="blue" variant="filled">
                                      Processing
                                    </Badge>
                                  )}
                                  {aiSession?.state === 'transcribing' && (
                                    <Badge color="violet" variant="filled">
                                      Transcribing
                                    </Badge>
                                  )}
                                </Group>

                                {/* Recording duration and level */}
                                {aiSession && (
                                  <Group gap="xs">
                                    <Text size="xs" c="dimmed">
                                      Duration: {formatDuration(Math.floor((aiSession.durationMs || 0) / 1000))}
                                    </Text>
                                    {aiSession.state === 'recording' && (
                                      <Box
                                        style={{
                                          width: 60,
                                          height: 4,
                                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                          borderRadius: 2,
                                          overflow: 'hidden',
                                        }}
                                      >
                                        <Box
                                          style={{
                                            width: `${aiSession.audioLevel}%`,
                                            height: '100%',
                                            backgroundColor: 'var(--mantine-color-green-5)',
                                            transition: 'width 100ms ease',
                                          }}
                                        />
                                      </Box>
                                    )}
                                  </Group>
                                )}

                                {/* Control buttons */}
                                <Group gap="xs">
                                  {!aiSession || aiSession.state === 'idle' ? (
                                    <Button
                                      leftSection={<IconMicrophone size={16} />}
                                      onClick={handleStartTranscription}
                                      color="red"
                                      variant="filled"
                                      fullWidth
                                    >
                                      Start Recording
                                    </Button>
                                  ) : aiSession.state === 'recording' ? (
                                    <>
                                      <Button
                                        leftSection={<IconPlayerPause size={16} />}
                                        onClick={pauseAIRecording}
                                        variant="light"
                                        color="yellow"
                                        style={{ flex: 1 }}
                                      >
                                        Pause
                                      </Button>
                                      <Button
                                        leftSection={<IconWand size={16} />}
                                        onClick={stopAIRecording}
                                        variant="filled"
                                        color="violet"
                                        style={{ flex: 1 }}
                                      >
                                        End Notes
                                      </Button>
                                    </>
                                  ) : aiSession.state === 'paused' ? (
                                    <>
                                      <Button
                                        leftSection={<IconPlayerPlay size={16} />}
                                        onClick={resumeAIRecording}
                                        variant="light"
                                        color="green"
                                        style={{ flex: 1 }}
                                      >
                                        Resume
                                      </Button>
                                      <Button
                                        leftSection={<IconWand size={16} />}
                                        onClick={stopAIRecording}
                                        variant="filled"
                                        color="violet"
                                        style={{ flex: 1 }}
                                      >
                                        End Notes
                                      </Button>
                                    </>
                                  ) : (
                                    <Center w="100%">
                                      <Group gap="xs">
                                        <Loader size="sm" />
                                        <Text size="sm">{aiSession.statusMessage || 'Processing...'}</Text>
                                      </Group>
                                    </Center>
                                  )}
                                </Group>

                                {/* Cancel button when recording/paused */}
                                {aiSession && (aiSession.state === 'recording' || aiSession.state === 'paused') && (
                                  <Button
                                    variant="subtle"
                                    color="gray"
                                    size="xs"
                                    onClick={cancelAIRecording}
                                  >
                                    Cancel Recording
                                  </Button>
                                )}
                              </Stack>
                            </Paper>

                            {/* Transcription Job Status */}
                            {currentTranscriptionJob && (
                              <Paper p="md" radius="md" className={classes.infoCard}>
                                <Stack gap="sm">
                                  <Group justify="space-between">
                                    <Text size="sm" fw={600}>
                                      Transcription Job
                                    </Text>
                                    {currentTranscriptionJob.status === 'processing' && (
                                      <Badge color="blue" variant="light">
                                        Processing
                                      </Badge>
                                    )}
                                    {currentTranscriptionJob.status === 'completed' && (
                                      <Badge color="green" variant="light" leftSection={<IconCheck size={10} />}>
                                        Complete
                                      </Badge>
                                    )}
                                    {currentTranscriptionJob.status === 'failed' && (
                                      <Badge color="red" variant="light">
                                        Failed
                                      </Badge>
                                    )}
                                  </Group>

                                  <Text size="xs" c="dimmed">
                                    {currentTranscriptionJob.statusMessage}
                                  </Text>

                                  {currentTranscriptionJob.status === 'completed' && currentTranscriptionJob.generatedNote && (
                                    <Button
                                      leftSection={<IconWand size={16} />}
                                      onClick={handleApplyAINote}
                                      variant="filled"
                                      color="violet"
                                      fullWidth
                                    >
                                      Apply AI Note
                                    </Button>
                                  )}

                                  {currentTranscriptionJob.status === 'failed' && (
                                    <Text size="xs" c="red">
                                      {currentTranscriptionJob.error || 'An error occurred during transcription.'}
                                    </Text>
                                  )}
                                </Stack>
                              </Paper>
                            )}

                            {/* Transcription Result */}
                            {transcriptionText && (
                              <Paper p="md" radius="md" className={classes.infoCard}>
                                <Stack gap="sm">
                                  <Text size="sm" fw={600}>
                                    Transcription
                                  </Text>
                                  <ScrollArea h={150}>
                                    <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>
                                      {transcriptionText}
                                    </Text>
                                  </ScrollArea>
                                </Stack>
                              </Paper>
                            )}

                            {/* Generated AI Note */}
                            {generatedAINote && (
                              <Paper p="md" radius="md" className={classes.infoCard}>
                                <Stack gap="sm">
                                  <Group justify="space-between">
                                    <Text size="sm" fw={600}>
                                      AI Generated Note
                                    </Text>
                                    <Badge color="violet" variant="light" size="xs">
                                      AWS HealthScribe
                                    </Badge>
                                  </Group>
                                  <ScrollArea h={200}>
                                    <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>
                                      {generatedAINote}
                                    </Text>
                                  </ScrollArea>
                                  <Button
                                    leftSection={<IconNotes size={16} />}
                                    onClick={handleApplyAINote}
                                    variant="light"
                                    fullWidth
                                  >
                                    Add to Notes
                                  </Button>
                                </Stack>
                              </Paper>
                            )}

                            {/* Info when no recording yet */}
                            {!aiSession && !transcriptionText && !generatedAINote && !currentTranscriptionJob && (
                              <Paper p="md" radius="md" className={classes.infoCard}>
                                <Stack gap="xs">
                                  <Text size="xs" c="dimmed">
                                    <strong>AI Transcription</strong> uses AWS HealthScribe to:
                                  </Text>
                                  <Stack gap={4}>
                                    <Group gap="xs">
                                      <IconCheck size={12} color="var(--mantine-color-green-5)" />
                                      <Text size="xs" c="dimmed">Transcribe the conversation</Text>
                                    </Group>
                                    <Group gap="xs">
                                      <IconCheck size={12} color="var(--mantine-color-green-5)" />
                                      <Text size="xs" c="dimmed">Identify speaker roles</Text>
                                    </Group>
                                    <Group gap="xs">
                                      <IconCheck size={12} color="var(--mantine-color-green-5)" />
                                      <Text size="xs" c="dimmed">Generate structured clinical notes</Text>
                                    </Group>
                                    <Group gap="xs">
                                      <IconCheck size={12} color="var(--mantine-color-green-5)" />
                                      <Text size="xs" c="dimmed">Extract clinical entities with Comprehend Medical</Text>
                                    </Group>
                                  </Stack>
                                </Stack>
                              </Paper>
                            )}
                          </>
                        )}
                      </Stack>
                    )}
                  </ScrollArea>

                  {/* Sidebar Footer - Quick Actions */}
                  <Box className={classes.sidebarFooter}>
                    <Group grow gap="xs">
                      <Tooltip label="AI Transcription">
                        <ActionIcon
                          variant={aiSession?.state === 'recording' ? 'filled' : 'light'}
                          color={aiSession?.state === 'recording' ? 'red' : 'gray'}
                          size="lg"
                          radius="md"
                          onClick={() => setSidebarTab('transcription')}
                        >
                          {aiSession?.state === 'recording' ? (
                            <IconMicrophone size={18} />
                          ) : (
                            <IconWand size={18} />
                          )}
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Add note">
                        <ActionIcon
                          variant="light"
                          color="gray"
                          size="lg"
                          radius="md"
                          onClick={() => {
                            setSidebarTab('notes');
                          }}
                        >
                          <IconNotes size={18} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="View full chart">
                        <ActionIcon
                          variant="light"
                          color="gray"
                          size="lg"
                          radius="md"
                          onClick={handleViewChart}
                        >
                          <IconExternalLink size={18} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="In-call chat">
                        <ActionIcon
                          variant="light"
                          color="gray"
                          size="lg"
                          radius="md"
                          onClick={() => setSidebarTab('chat')}
                        >
                          <IconMessageCircle size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Box>
                </Stack>
              </Box>
            )}
          </Transition>
        </Box>

        {/* Bottom Call Controls */}
        <Transition mounted={showControls} transition="slide-up" duration={200}>
          {(styles) => (
            <Box className={classes.controlsBar} style={styles}>
              <CallControls
                isMuted={activeCall.isMuted}
                isCameraOff={activeCall.isCameraOff}
                isScreenSharing={activeCall.isScreenSharing}
                isVideoCall={isVideoCall}
                onToggleMute={toggleMute}
                onToggleCamera={toggleCamera}
                onToggleScreenShare={toggleScreenShare}
                onEndCall={endCall}
              />
            </Box>
          )}
        </Transition>

        {/* Save Note Modal */}
        <Modal
          opened={showNotesModal}
          onClose={() => setShowNotesModal(false)}
          title="Save Telehealth Note"
          centered
        >
          <Stack gap="md">
            <TextInput
              label="Note Title (optional)"
              placeholder="e.g., Follow-up discussion"
              value={noteTitle}
              onChange={(e) => setNoteTitle(e.target.value)}
            />

            <Textarea
              label="Note Content"
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              minRows={6}
              autosize
            />

            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" onClick={() => setShowNotesModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveNote}
                loading={isSavingNote}
                disabled={!noteContent.trim()}
              >
                Save Note
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Portal>
  );
}

export default VideoCallOverlay;
